import { compact, concat } from 'lodash-es';

const MAX_THUMBNAIL_COUNT = 5;
const IDEAL_INTERIOR_THUMBNAIL_COUNT = 3;
const IDEAL_EXTERIOR_THUMBNAIL_COUNT = 1;

interface CalculateThumbnailsArgs {
  exteriorImageUrls: string[];
  interiorImageUrls: string[];
  interior360?: string;
}

export function calculateThumbnails({
  exteriorImageUrls,
  interiorImageUrls,
  interior360,
}: CalculateThumbnailsArgs) {
  // We prioritize the exterior third shot if possible.
  const prioritizedExteriorThumbnailUrls = compact([
    exteriorImageUrls[2], // Prefer exterior third shot as first thumbnail
  ]);
  const potentialExteriorFallbackThumbnailUrls = concat(
    exteriorImageUrls.slice(0, 2),
    exteriorImageUrls.slice(3)
  );

  // We prioritize having 3 interior thumbnails if possible.
  const prioritizedInteriorThumbnailUrls = compact([
    interiorImageUrls[0], // Prefer interior first shot as second thumbnail
    interiorImageUrls[1], // Prefer interior second shot as third thumbnail
    interiorImageUrls[2], // Prefer interior third shot as fourth thumbnail
  ]);
  const potentialInteriorFallbackThumbnailUrls = interiorImageUrls.slice(3);

  /**
   * Our thumbnail backfilling strategy has 2 phases:
   *
   * Phase 1:
   * If we are missing interior thumbnails, try to fill to 3 with our fallback interior thumbnails.
   * If we are missing the exterior side thumbnail, try to fill with any fallback exterior thumbnail.
   * If we are missing the 360 interior thumbnail, try to fill with our leftover fallback interior thumbnails.
   *
   * Phase 2:
   * If we are still short of 5 thumbnails after Phase 1, try to fill with our leftover exterior thumbnails first, then with our leftover interior thumbnails.
   */

  /**
   * PHASE 1
   */
  const fallbackInteriors = [];
  const fallbackExteriors = [];

  // Update fallback interiors if we are missing interior thumbnails.
  const missingInteriorThumbnails =
    IDEAL_INTERIOR_THUMBNAIL_COUNT - prioritizedInteriorThumbnailUrls.length;
  if (missingInteriorThumbnails > 0) {
    for (let i = 0; i < missingInteriorThumbnails; i++) {
      const interiorFallbackThumbnailUrl = potentialInteriorFallbackThumbnailUrls.shift();
      if (interiorFallbackThumbnailUrl) {
        fallbackInteriors.push(interiorFallbackThumbnailUrl);
      }
    }
  }

  // Update fallback exteriors if we are missing exterior thumbnail.
  if (prioritizedExteriorThumbnailUrls.length < IDEAL_EXTERIOR_THUMBNAIL_COUNT) {
    const exteriorFallbackThumbnailUrl = potentialExteriorFallbackThumbnailUrls.shift();
    if (exteriorFallbackThumbnailUrl) {
      fallbackExteriors.push(exteriorFallbackThumbnailUrl);
    }
  }

  // Update fallback interiors if we are missing 360 interior thumbnail.
  if (!interior360) {
    const interiorFallbackThumbnailUrl = potentialInteriorFallbackThumbnailUrls.shift();
    if (interiorFallbackThumbnailUrl) {
      fallbackInteriors.push(interiorFallbackThumbnailUrl);
    }
  }

  /**
   * PHASE 2
   */
  const interior360ThumbnailCount = interior360 ? 1 : 0;

  // If we are still short of 5 thumbnails after Phase 1, fill with exterior thumbnails first, then interior thumbnails.
  while (
    prioritizedInteriorThumbnailUrls.length +
      fallbackInteriors.length +
      prioritizedExteriorThumbnailUrls.length +
      fallbackExteriors.length +
      interior360ThumbnailCount <
    MAX_THUMBNAIL_COUNT
  ) {
    const exteriorFallbackThumbnailUrl = potentialExteriorFallbackThumbnailUrls.shift();
    if (exteriorFallbackThumbnailUrl) {
      fallbackExteriors.push(exteriorFallbackThumbnailUrl);
    } else {
      // If we run out of exterior thumbnails to add, we try to fill with interior thumbnails.
      const interiorFallbackThumbnailUrl = potentialInteriorFallbackThumbnailUrls.shift();
      if (interiorFallbackThumbnailUrl) {
        fallbackInteriors.push(interiorFallbackThumbnailUrl);
      } else {
        // If we run out of both interior and exterior thumbnails to add, we exit the loop.
        break;
      }
    }
  }

  const thumbnailCount =
    prioritizedExteriorThumbnailUrls.length +
    prioritizedInteriorThumbnailUrls.length +
    fallbackExteriors.length +
    fallbackInteriors.length +
    interior360ThumbnailCount;

  return {
    exteriorThumbnailUrl: prioritizedExteriorThumbnailUrls[0],
    interiorThumbnailUrls: prioritizedInteriorThumbnailUrls,
    // The backfilled thumbnail lists should not have any falsy values
    fallbackExteriors,
    fallbackInteriors,
    thumbnailCount,
  };
}
